/**
 * Configuration Utility
 * Reads BACKEND_URL from env.json
 * DevOps can modify BACKEND_URL in env.json after build without rebuilding
 * Simple structure: { "BACKEND_URL": "https://api.customer.com/api/web" }
 */

let envConfig = null;
let envConfigLoading = false;
let envConfigPromise = null;

/**
 * Load env.json configuration file
 * @returns {Promise<Object>} Environment configuration
 */
async function loadEnvConfigFromFile() {
  if (envConfig) {
    return envConfig;
  }

  if (envConfigLoading && envConfigPromise) {
    return envConfigPromise;
  }

  envConfigLoading = true;
  envConfigPromise = fetch('/env.json')
    .then((response) => {
      if (!response.ok) {
        throw new Error('Failed to load env.json');
      }
      return response.json();
    })
    .then((data) => {
      envConfig = data;
      envConfigLoading = false;
      // Set global flag for debugging
      if (typeof window !== 'undefined') {
        window.__ENV_CONFIG__ = envConfig;
        window.__BACKEND_URL__ = data.BACKEND_URL || data.backendUrl;
      }
      return data;
    })
    .catch((error) => {
      console.warn('Failed to load env.json, using default:', error);
      envConfig = {
        BACKEND_URL: '/api/web',
      };
      envConfigLoading = false;
      return envConfig;
    });

  return envConfigPromise;
}

/**
 * Get the base API URL from configuration
 * Reads BACKEND_URL from env.json, defaults to '/api/web'
 * For localhost, uses BACKEND_URL_LOCAL if available, otherwise uses localhost URL
 * @returns {Promise<string>} Base API URL
 */
export async function getBaseUrlAsync() {
  // Load env.json configuration
  const env = await loadEnvConfigFromFile();

  // Check if we're on localhost
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1');

  if (isLocalhost) {
    // For localhost, prefer BACKEND_URL_LOCAL if available
    if (env.BACKEND_URL_LOCAL || env.backendUrlLocal) {
      const url = env.BACKEND_URL_LOCAL || env.backendUrlLocal;
      console.log('✅ Using BACKEND_URL_LOCAL from env.json:', url);
      return url;
    }
    // Otherwise use localhost URL
    const localhostUrl = 'http://localhost:3050/api/web';
    console.log('✅ Using localhost URL:', localhostUrl);
    return localhostUrl;
  }

  // For production, use BACKEND_URL from env.json
  const url = env.BACKEND_URL || env.backendUrl || '/api/web';
  console.log('✅ Using BACKEND_URL from env.json:', url);
  return url;
}

/**
 * Get the base API URL from configuration (synchronous version)
 * Uses cached env config if available, otherwise returns default
 * For accurate results, use getBaseUrlAsync() instead
 * @returns {string} Base API URL
 */
export function getBaseUrl() {
  // If env config is already loaded, use it
  if (envConfig) {
    // Check if we're on localhost
    const isLocalhost =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1');

    if (isLocalhost) {
      // For localhost, prefer BACKEND_URL_LOCAL if available
      if (envConfig.BACKEND_URL_LOCAL || envConfig.backendUrlLocal) {
        return envConfig.BACKEND_URL_LOCAL || envConfig.backendUrlLocal;
      }
      // Otherwise use localhost URL
      return 'http://localhost:3050/api/web';
    }

    // For production, use BACKEND_URL from env.json
    const url = envConfig.BACKEND_URL || envConfig.backendUrl || '/api/web';
    return url;
  }

  // Default fallback - check if localhost
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1')
  ) {
    return 'http://localhost:3050/api/web';
  }

  return '/api/web';
}

/**
 * Load env.json configuration at startup
 * This is called in index.js to preload the configuration
 */
export async function loadEnvConfig() {
  await loadEnvConfigFromFile();
}
